import { QuoteFailure, QuoteSuccess } from "@b2bportal/purchase-api";
import {
  CardPaymentActions,
  CardPaymentEventTypes,
  CardPaymentServices,
  CartQuoteError,
  CartQuoteErrorModal,
  CartQuoteEventType,
  CartQuoteGuardTypes,
  CartQuoteState,
  CartState,
  combineServices,
  ContactEventType,
  ContactInformationActions,
  FlightPassengerActions,
  FlightPassengerEventTypes,
  GenericChildState,
  getCardPaymentMachine,
  getCartFulfillMachine,
  getCartQuoteMachine,
  getCartUpdateMachine,
  getContactInformationMachine,
  getFlightPassengerInformationMachine,
  getPassportMachineState,
  getReviewMachineState,
  getRewardsPaymentMachine,
  getSeatMachine,
  getWalletMachine,
  ParentContextWithWallet,
  ParentState,
  PassengerInformationChildState,
  PassengerInformationState,
  PaymentInformationChildState,
  PaymentInformationState,
  RewardsPaymentEventTypes,
  trackPollFailure,
  trackPollSuccess,
  trackQuotePollFailure,
  trackQuotePollSuccess,
  WalletActions,
  WalletEventTypes,
  WalletServices,
} from "@capone/checkout";
import {
  isCorpTenant,
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
} from "@capone/common";
import { buildPackageBookingData } from "../utils/buildPackageBookingData";
import { PRICE_QUOTE_PACKAGE } from "redmond";
import {
  assign,
  choose,
  createMachine,
  DoneInvokeEvent,
  MachineConfig,
  sendTo,
  spawn,
} from "xstate";
import { ActionTypes } from "./actions";
import {
  addPackageDiscount,
  addPackageProduct,
  setPaymentFulfillParams,
  setRedeemedPackageDiscountAmount,
} from "./actions/common";
// import { getAirDisruptionMachine } from "./childStateMachine/AirDisruption";
import { LodgingActionTypes } from "./actions/lodging";
import { Event, TEvent } from "./events";
import { Guards, GuardTypes } from "./guards";
import { PackagesCommonGuards } from "./guards/common";
import { getInitialPackagesMachineContext } from "./initialContext";
import { Services } from "./services";
import { PackagesMachineContext } from "./types";
import {
  getCorporatePollCompleteProperties,
  trackPollCompleteProperties,
} from "./utils";
import { config } from "../../../api/config";

export const packagesMachineId = "capone-packages-checkout";

const originalCardPaymentMachine = getCardPaymentMachine({
  parentMachineId: packagesMachineId,
  nextState: `#${packagesMachineId}.${ParentState.cartUpdateBeforeFulfill}`,
  previousState: `#${packagesMachineId}.${ParentState.seatSelection}`,
});
const cardPaymentMachine = {
  ...originalCardPaymentMachine,
  states: {
    ...originalCardPaymentMachine.states,
    [PaymentInformationState.add]: {
      initial: PaymentInformationChildState.idle,
      states: {
        [PaymentInformationChildState.idle]: {},
        [PaymentInformationChildState.verify]: {
          invoke: {
            src: CardPaymentServices.verifyPayment,
            onDone: {
              actions: [ActionTypes.setPaymentMethodId],
              target: `#${packagesMachineId}.${ParentState.cardPayment}.${PaymentInformationState.idle}`,
            },
            onError: {
              actions: ActionTypes.setPaymentError,
              target: `#${packagesMachineId}.${ParentState.cardPayment}.${PaymentInformationState.error}`,
            },
          },
        },
      },
    },
  },
  on: {
    ...originalCardPaymentMachine.on,
    [CardPaymentEventTypes.NEXT]: [
      {
        target: `#${packagesMachineId}.${ParentState.cartUpdateBeforeFulfill}`,
        cond: PackagesCommonGuards.isDesktop,
      },
      {
        target: `#${packagesMachineId}.${ParentState.review}`,
        cond: PackagesCommonGuards.isMobile,
      },
    ],
    [CardPaymentEventTypes.PREVIOUS]: [
      {
        target: `#${packagesMachineId}.${ParentState.seatSelection}`,
        cond: GuardTypes.hasSeatMapAvailability,
      },
      {
        target: `#${packagesMachineId}.${ParentState.seatSelection}`,
        cond: (ctx: PackagesMachineContext) =>
          PackagesCommonGuards.isMobile(ctx) &&
          PackagesCommonGuards.isPackageCheckoutOptimizationsV1Experiment(ctx),
      },
      {
        target: `#${packagesMachineId}.${ParentState.contactInformation}`,
        cond: GuardTypes.isMobile,
      },
      {
        target: `#${packagesMachineId}.${ParentState.lodgingPassengerInformation}`,
        cond: GuardTypes.isDesktop,
      },
    ],
  },
};

export const packagesBookStateMachine: MachineConfig<
  PackagesMachineContext,
  any,
  TEvent
> = {
  schema: {
    context: getInitialPackagesMachineContext(),
    events: {} as TEvent,
  },

  // Machine identifier
  id: packagesMachineId,

  // Initial state
  initial: "waitForState",

  // State definitions

  states: {
    waitForState: {},
    finalizePackage: {
      initial: "finalizing",
      states: {
        finalizing: {
          always: [
            {
              cond: Guards.hasFinalizePackageResponse,
              target: `#${packagesMachineId}.${ParentState.loading}`,
            },
          ],
          invoke: {
            src: Services.finalizePackageService,
            onDone: {
              actions: ActionTypes.setFinalizePackageResponse,
              target: `#${packagesMachineId}.${ParentState.loading}`,
            },
            onError: {
              actions: ActionTypes.setFinalizePackageCallStateFailed,
              target: `#${packagesMachineId}.${ParentState.loading}`,
            },
          },
        },
      },
    },
    [ParentState.loading]: {
      entry: assign({
        [ParentState.loading]: (ctx: PackagesMachineContext) => {
          if (!ctx[ParentState.loading].ref) {
            return {
              ref: spawn(
                createMachine({
                  id: ParentState.loading,
                  context: ctx,
                  initial: `loading`,
                  states: {
                    loading: {
                      type: `parallel`,
                      onDone: {
                        actions: sendTo(packagesMachineId, {
                          type: Event.NEXT,
                        }),
                      },
                      states: {
                        listPaymentMethods: {
                          initial: `loading`,
                          states: {
                            loading: {
                              invoke: {
                                src: Services.listPaymentMethods,
                                onDone: {
                                  actions:
                                    CardPaymentActions.handleListPayments,
                                  target: `loaded`,
                                },
                                onError: {
                                  actions: CardPaymentActions.setPaymentError,
                                },
                              },
                            },
                            loaded: {
                              type: "final",
                            },
                          },
                        },
                        fetchContactInfo: {
                          initial: `loading`,
                          states: {
                            loading: {
                              invoke: {
                                src: Services.fetchContactInfoService,
                                onDone: {
                                  actions:
                                    ContactInformationActions.setContactInfo,
                                  target: `loaded`,
                                },
                                onError: {},
                              },
                            },
                            loaded: {
                              type: "final",
                            },
                          },
                        },
                        fetchTripPricing: {
                          initial: `loading`,
                          states: {
                            loading: {
                              invoke: {
                                src: Services.fetchTripPricingService,
                                onDone: {
                                  actions:
                                    FlightPassengerActions.setTripPricingFromData,
                                  target: `loaded`,
                                },
                                onError: {
                                  actions:
                                    FlightPassengerActions.setTripPricingError,
                                },
                              },
                            },
                            loaded: {
                              type: "final",
                            },
                          },
                        },
                        fetchTravelWalletItems: {
                          initial: `loading`,
                          states: {
                            loading: {
                              invoke: {
                                src: (
                                  loadingContext: ParentContextWithWallet,
                                ) =>
                                  combineServices([
                                    WalletServices.getWalletOffers(
                                      loadingContext,
                                    ),
                                    WalletServices.getWalletCredits(
                                      loadingContext,
                                    ),
                                    WalletServices.getWalletCreditHistory(
                                      loadingContext,
                                    ),
                                  ]),
                                onDone: {
                                  actions: [
                                    WalletActions.handleGetWalletOffers,
                                    WalletActions.setCreditAmountToApply,
                                  ],
                                  target: `loaded`,
                                },
                                onError: {
                                  actions: WalletActions.setWalletError,
                                },
                              },
                            },
                            loaded: {
                              type: "final",
                            },
                          },
                        },
                      },
                    },
                  },
                }),
              ),
            };
          }

          return { ...ctx[ParentState.loading] };
        },
      }),
      on: {
        [Event.NEXT]: {
          target: `#${packagesMachineId}.${ParentState.passengerInformation}`,
        },
      },
    },

    [ParentState.passengerInformation]: {
      ...getFlightPassengerInformationMachine({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.lodgingPassengerInformation}`,
        nextStateFromReview: `#${packagesMachineId}.${ParentState.lodgingPassengerInformation}`,
      }),
    },
    [ParentState.contactInformation]: {
      ...getContactInformationMachine({
        parentMachineId: packagesMachineId,
        previousState: `#${packagesMachineId}.${ParentState.lodgingPassengerInformation}`,
        onDone: [
          {
            cond: PackagesCommonGuards.isDesktop,
            target: `#${packagesMachineId}.${ParentState.lodgingPassengerInformation}`,
          },
          {
            cond: PackagesCommonGuards.isMobile,
            target: `#${packagesMachineId}.${ParentState.cartQuote}`,
          },
        ],
        onError: [
          {
            cond: PackagesCommonGuards.isDesktop,
            target: `#${packagesMachineId}.${ParentState.lodgingPassengerInformation}`,
          },
          {
            cond: PackagesCommonGuards.isMobile,
            target: `#${packagesMachineId}.${ParentState.cartQuote}`,
          },
        ],
      }),
    },
    [ParentState.passport]: {
      ...getPassportMachineState({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.passengerInformation}`,
        previousState: `#${packagesMachineId}.${ParentState.passengerInformation}`,
      }),
    },
    [ParentState.lodgingPassengerInformation]: {
      initial: PassengerInformationState.passengerPicker,
      states: {
        [PassengerInformationState.passengerPicker]: {
          initial: PassengerInformationChildState.idle,
          states: {
            [PassengerInformationChildState.idle]: {},
            [PassengerInformationChildState.updateMultiple]: {
              invoke: {
                src: (ctx: PackagesMachineContext) =>
                  Services.updateUserPassengersMultipleService(
                    ctx[ParentState.passengerInformation],
                  ),
                onDone: [
                  {
                    target: `#${packagesMachineId}.${ParentState.cartQuote}`,
                    cond: PackagesCommonGuards.isDesktop,
                  },
                  {
                    target: `#${packagesMachineId}.${ParentState.contactInformation}`,
                    cond: PackagesCommonGuards.isMobile,
                  },
                ],
                // TODO: error handle
                onError: [
                  {
                    target: `#${packagesMachineId}.${ParentState.cartQuote}`,
                    cond: PackagesCommonGuards.isDesktop,
                  },
                  {
                    target: `#${packagesMachineId}.${ParentState.contactInformation}`,
                    cond: PackagesCommonGuards.isMobile,
                  },
                ],
              },
            },
          },
        },
        [PassengerInformationState.error]: {},
      },
      on: {
        [Event.NEXT]: [
          {
            target: `#${packagesMachineId}.${ParentState.lodgingPassengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.updateMultiple}`,
          },
        ],
        [Event.PREVIOUS]: [
          {
            target: `#${packagesMachineId}.${ParentState.passengerInformation}`,
          },
        ],
        [FlightPassengerEventTypes.SELECT_PASSENGER]: [
          {
            actions: [LodgingActionTypes.selectPassenger],
          },
        ],
        [FlightPassengerEventTypes.SET_USER_SELECTED_TRAVELERS_LIST]: [
          {
            actions: [ActionTypes.setUserSelectedTravelersList],
          },
        ],
      },
    },
    [ParentState.cartQuote]: {
      entry: [
        ActionTypes.addFlightProduct,
        LodgingActionTypes.addLodgingProduct,
        addPackageProduct,
      ],
      ...getCartQuoteMachine({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.cartUpdateForSeats}`,
        trackPollSuccessAction: trackQuotePollSuccess({
          eventName: PRICE_QUOTE_PACKAGE,
          getAdditionalProperties: (context: PackagesMachineContext, _) => ({
            ...context.cartQuote.trackingProperties,
            unavailable_products: context.cartQuote.unavailableProducts,
            ...(isCorpTenant(config.TENANT) &&
              getCorporatePollCompleteProperties(context)),
          }),
        }),
        trackPollFailureAction: trackQuotePollFailure({
          eventName: PRICE_QUOTE_PACKAGE,

          getAdditionalProperties: (
            context: PackagesMachineContext,
            event: DoneInvokeEvent<
              | QuoteSuccess
              | {
                  data: Array<QuoteFailure>;
                  type: CartQuoteErrorModal;
                }
            >,
          ) => ({
            type: (event.data as CartQuoteError).type,
            ...(isCorpTenant(config.TENANT) &&
              getCorporatePollCompleteProperties(context)),
          }),
        }),
      }),
      exit: [ActionTypes.checkForPriceChange, addPackageDiscount],
    },
    // Add Products to Cart
    // [ParentState.cancelForAnyReason]: {
    //   ...getCancelForAnyReasonMachine({
    //     parentMachineId: packagesMachineId,
    //     nextState: `#${packagesMachineId}.${ParentState.disruption}`,
    //   }),
    // },
    // [ParentState.changeForAnyReason]: {
    //   ...getChfarMachine({
    //     parentMachineId: packagesMachineId,
    //     nextState: `#${packagesMachineId}.${ParentState.disruption}`,
    //   }),
    // },
    // [ParentState.disruption]: {
    //   ...getAirDisruptionMachine({
    //     parentMachineId: packagesMachineId,
    //     nextState: `#${packagesMachineId}.${ParentState.priceDrop}`,
    //   }),
    // },
    // [ParentState.priceDrop]: {
    //   ...getAirPriceDropMachine({
    //     parentMachineId: packagesMachineId,
    //     nextState: `#${packagesMachineId}.${ParentState.vipSupport}`,
    //   }),
    // },
    // [ParentState.vipSupport]: {
    //   ...getVipSupportMachine({
    //     parentMachineId: packagesMachineId,
    //     nextState: `#${packagesMachineId}.${ParentState.priceFreezeDiscount}`,
    //   }),
    // },
    // Add Discounts to Cart
    // [ParentState.priceFreezeDiscount]: {
    //   ...getPriceFreezeDiscountMachineState({
    //     parentMachineId: packagesMachineId,
    //     nextState: `#${packagesMachineId}.${ParentState.wallet}`,
    //   }),
    // },
    [ParentState.wallet]: {
      ...getWalletMachine({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.cartUpdate}`,
      }),
    },

    // Update cart with Products and Discounts
    [ParentState.cartUpdate]: {
      ...getCartUpdateMachine({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.cardPayment}`,
        exit: [
          choose([
            {
              cond: PackagesCommonGuards.isPackageCheckoutOptimizationsV1Experiment,
              actions: [WalletActions.setCreditAmountToApply],
            },
          ]),
        ],
      }),
    },
    [ParentState.seatSelection]: {
      ...getSeatMachine({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.wallet}`,
        previousState: `#${packagesMachineId}.${ParentState.contactInformation}`,
        idleStateCondition: (ctx: PackagesMachineContext) =>
          PackagesCommonGuards.isMobile(ctx) &&
          PackagesCommonGuards.isPackageCheckoutOptimizationsV1Experiment(ctx),
      }),
    },
    // Update cart before seats in addition to after (only called if there are changes)
    // This is to add the synthetic discount (offer) for accurate pricing
    [ParentState.cartUpdateForSeats]: {
      ...getCartUpdateMachine({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.seatSelection}`,
        exit: [
          choose([
            {
              cond: PackagesCommonGuards.isPackageCheckoutOptimizationsV1Experiment,
              actions: [WalletActions.setCreditAmountToApply],
            },
          ]),
        ],
      }),
    },
    [ParentState.rewardsPayment]: {
      ...getRewardsPaymentMachine({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.cardPayment}`,
        previousState: `#${packagesMachineId}.${ParentState.seatSelection}`,
      }),
    },
    [ParentState.cardPayment]: cardPaymentMachine,
    [ParentState.review]: {
      initial: GenericChildState.idle,
      states: {
        [GenericChildState.loading]: {
          //   invoke: {
          //     src: ServiceTypes.trackReviewDetails,
          //     onDone: `#${packagesMachineId}.${ParentState.review}.${GenericChildState.idle}`,
          //     onError: `#${packagesMachineId}.${ParentState.review}.${GenericChildState.idle}`,
          //   },
        },
        [GenericChildState.idle]: {},
      },
      ...getReviewMachineState({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.cartUpdateBeforeFulfill}`,
        previousState: `#${packagesMachineId}.${ParentState.cardPayment}`,
      }),
      //   exit: ActionTypes.removeVipProductFromCart,
    },
    [ParentState.cartUpdateBeforeFulfill]: {
      ...getCartUpdateMachine({
        entry: [addPackageDiscount, WalletActions.addFinalizedPaymentDiscounts],
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.cartFulfill}`,
      }),
    },
    [ParentState.cartFulfill]: {
      entry: [setPaymentFulfillParams, setRedeemedPackageDiscountAmount],
      ...getCartFulfillMachine<PackagesMachineContext>({
        parentMachineId: packagesMachineId,
        nextState: `#${packagesMachineId}.${ParentState.bookingConfirmation}`,
        trackPollSuccessAction: trackPollSuccess({
          ...trackPollCompleteProperties,
          onDone: () => {
            travelAppInterface.sendEvent({
              name: TravelAppInterfaceEventName.BOOKING_COMPLETE,
              funnel: TravelAppInterfaceFunnelType.PACKAGES,
              data: buildPackageBookingData(),
            });
          },
        }),
        trackPollFailureAction: trackPollFailure(trackPollCompleteProperties),
      }),
    },
    [ParentState.bookingConfirmation]: {},
  },
  on: {
    [Event.OPEN_PASSENGER_PICKER]: `#${packagesMachineId}.${ParentState.passengerInformation}`,
    // wallet credit Payment Events,
    [WalletEventTypes.SET_SELECTED_OFFER]: {
      actions: ActionTypes.addDiscounts,
    },
    [WalletEventTypes.REMOVE_SELECTED_OFFER]: {
      actions: ActionTypes.removeDiscounts,
    },
    [WalletEventTypes.SET_CREDIT_AMOUNT_TO_APPLY]: {
      actions: ActionTypes.setCreditAmountToApply,
    },
    // Rewards Payment Events,
    [RewardsPaymentEventTypes.SET_SELECTED_REWARDS_ACCOUNT_ID]: {
      actions: ActionTypes.setSelectedRewardsAccountId,
    },
    [RewardsPaymentEventTypes.SET_REWARDS_FIAT_AMOUNT_TO_PAY]: {
      actions: ActionTypes.setRewardsFiatAmountToPay,
    },
    [RewardsPaymentEventTypes.SET_REWARDS_AMOUNT_TO_PAY]: {
      actions: ActionTypes.setRewardsAmountToPay,
    },
    [RewardsPaymentEventTypes.SET_REWARDS_PAYMENT_VISITED]: {
      actions: ActionTypes.setRewardsPaymentVisited,
    },
    [RewardsPaymentEventTypes.SET_REWARDS_ACCOUNTS]: {
      actions: ActionTypes.setRewardsAccounts,
    },
    [RewardsPaymentEventTypes.SET_EARN_BY_REWARDS_ACCOUNT_ID]: {
      actions: ActionTypes.setEarnValueForAccount,
    },
    // Card Payment Events
    [CardPaymentEventTypes.SET_SELECTED_PAYMENT_METHOD_ID]: {
      actions: ActionTypes.setSelectedPaymentMethodId,
    },
    [CardPaymentEventTypes.DELETE_PAYMENT_METHOD]: `#${packagesMachineId}.${ParentState.cardPayment}.${PaymentInformationState.delete}`,
    [CardPaymentEventTypes.OPEN_PAYMENT_FORM]: `#${packagesMachineId}.${ParentState.cardPayment}.${PaymentInformationState.add}`,
    [CardPaymentEventTypes.CLOSE_PAYMENT_FORM]: `#${packagesMachineId}.${ParentState.cardPayment}.${PaymentInformationState.idle}`,
    [CardPaymentEventTypes.VERIFY_PAYMENT_METHOD]: {
      actions: ActionTypes.setSpreedlyToken,
      target: `#${packagesMachineId}.${ParentState.cardPayment}.${PaymentInformationState.add}.${PaymentInformationChildState.verify}`,
    },
    [CardPaymentEventTypes.ACKNOWLEDGE_PRICE_CHANGE]: {
      actions: ActionTypes.acknowledgePriceChange,
    },
    // Contact Information Events
    [ContactEventType.CHANGE]: {
      actions: ActionTypes.setContactInfo,
    },
    // Events below are used for browser navigation
    [Event.GO_TO_PASSENGER_SELECT]: `#${packagesMachineId}.${ParentState.passengerInformation}`,
    [Event.GO_TO_CONTACT_INFORMATION]: `#${packagesMachineId}.${ParentState.contactInformation}`,
    [Event.GO_TO_HOTEL_PASSENGER_SELECT]: `#${packagesMachineId}.${ParentState.lodgingPassengerInformation}`,
    [Event.GO_TO_CART_QUOTE]: `#${packagesMachineId}.${ParentState.cartQuote}`,
    [Event.GO_TO_REVIEW]: `#${packagesMachineId}.${ParentState.review}`,
    [Event.GO_TO_CARD_PAYMENT]: `#${packagesMachineId}.${ParentState.cardPayment}`,
    [Event.GO_TO_BOOKING_CONFIRMATION]: `#${packagesMachineId}.${ParentState.bookingConfirmation}`,
    [Event.GO_TO_SEAT_SELECTION]: [
      {
        target: `#${packagesMachineId}.${ParentState.cartQuote}`,
        cond: CartQuoteGuardTypes.isCartLocked,
      },
      {
        target: `#${packagesMachineId}.${ParentState.seatSelection}`,
      },
    ],
    // [Event.CBA_SKIP_SEAT]: {
    //   actions: [
    //     ActionTypes.setSkipSeatSelection,
    //     ActionTypes.resetSelectedSeats,
    //   ],
    // },
    // [Event.CBA_SELECT_SEAT]: {
    //   actions: [ActionTypes.selectSeat, ActionTypes.resetSkippedSeatSelection],
    // },
    [FlightPassengerEventTypes.OPEN_FORM_AND_SET_PASSENGER]: {
      target: `#${packagesMachineId}.${ParentState.passengerInformation}`,
      actions: ActionTypes.openFormAndSetCurrentPassenger,
    },
    [Event.INITIALIZE_CHECKOUT_STATE]: {
      actions: ActionTypes.initializeCheckoutState,
      target: `#${packagesMachineId}.finalizePackage`,
    },
    // Under v2, the Book workflow components' "initialize checkout context
    // from redux" effect legitimately re-fires multiple times (e.g. once
    // tripDetails resolves asynchronously) — unlike INITIALIZE_CHECKOUT_STATE
    // above, which v0 only ever dispatches once. Gate on this being the
    // first dispatch so repeat dispatches still patch context via
    // initializeCheckoutState but don't re-enter finalizePackage.finalizing
    // and re-fire its `invoke` (the real finalize network call) each time.
    // markFinalizePackageInProcess sets finalizePackageCallState
    // synchronously so a dispatch racing in before the first call resolves
    // still fails the cond below.
    // Side effect: once a finalize attempt fails (finalizePackageCallState becomes
    // CallState.Failed, truthy), this cond is false for every later dispatch too, so this
    // specific re-entry path never retries finalize again for the lifetime of this machine
    // instance — confirm retry-on-failure is handled elsewhere (e.g. BookingErrorModal) if that's
    // relied upon.
    [Event.INITIALIZE_CHECKOUT_STATE_V2]: [
      {
        cond: (ctx: PackagesMachineContext) =>
          !ctx.finalizePackageResponse && !ctx.finalizePackageCallState,
        actions: [
          ActionTypes.initializeCheckoutState,
          ActionTypes.markFinalizePackageInProcess,
        ],
        target: `#${packagesMachineId}.finalizePackage`,
      },
      {
        actions: ActionTypes.initializeCheckoutState,
      },
    ],
    [Event.SET_PLATFORM]: {
      actions: ActionTypes.setPlatform,
    },
    [CartQuoteEventType.RETRY_QUOTE]: {
      target: `#${packagesMachineId}.${CartState.cartQuote}.${CartQuoteState.route}`,
      actions: [ActionTypes.setQuoteRetries, ActionTypes.clearCartQuoteError],
    },
    [Event.SET_TRIP_PURPOSE]: {
      actions: ActionTypes.setTripPurpose,
    },
    [Event.SET_TRIP_DESCRIPTION]: {
      actions: ActionTypes.setTripDescription,
    },
    [Event.SET_USER_PASSENGERS]: {
      actions: ActionTypes.setUserPassengers,
    },
    [Event.SET_INITIAL_BOOK_TIME]: {
      actions: ActionTypes.setInitialBookTime,
    },
  },
};
